import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { AnimatedLoginButton } from "@/components/animated-login-button"
import { AuthLegalLinks } from "@/components/auth/auth-legal-links"
import { AuthDivider } from "@/components/auth/auth-divider"
import { SocialAuthButtons } from "@/components/auth/social-auth-buttons"
import { LoginByCodeForm } from "@/components/auth/login-by-code-form"
import { socialAuthEnabled } from "@/constants/auth-social"
import { loginByCodeEnabled } from "@/constants/auth-login-code"
import { registrationClosed, getAccessRequestConfig, getSignupMode } from "@/constants/signup"
import { useAuth } from "@/hooks/use-auth"
import { CURRENT_CLIENT } from "@/constants"

interface LoginFormProps extends React.ComponentPropsWithoutRef<"div"> {
  onSuccess?: () => void;
}

type LoginLocationState = {
  message?: string
}

export function LoginForm({ className, onSuccess, ...props }: LoginFormProps) {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")
  const { login, isLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const state = location.state as LoginLocationState | null
    const message = state?.message?.trim()
    if (!message) {
      return
    }

    setSuccessMessage(message)
    navigate(location.pathname + location.search, { replace: true, state: null })
  }, [location.pathname, location.search, location.state, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setSuccessMessage("")

    if (!email || !password) {
      setError("Please fill in all fields")
      return
    }

    const result = await login(email, password)

    if (result.success) {
      onSuccess?.()
    } else if (result.approval_pending) {
      navigate('/register/pending')
    } else {
      setError(result.error || "Login failed")
    }
  }

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-6">
            {successMessage && (
              <div className="p-4 text-sm text-success border border-success/20 rounded-md bg-success/10">
                {successMessage}
              </div>
            )}
            {error && (
              <div className="p-4 text-sm text-destructive border border-destructive/20 rounded-md bg-destructive/10">
                {error}
              </div>
            )}
            {socialAuthEnabled() ? (
              <>
                <SocialAuthButtons disabled={isLoading} />
                <AuthDivider />
              </>
            ) : null}
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder={CURRENT_CLIENT.placeholderEmail}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            <div className="grid gap-2">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="password">Password</Label>
                <Button
                  type="button"
                  variant="link"
                  size="inlineLinkAux"
                  onClick={() => navigate('/auth/reset-password')}
                  disabled={isLoading}
                >
                  Forgot password?
                </Button>
              </div>
              <Input
                id="password"
                type="password"
                placeholder={CURRENT_CLIENT.placeholderPassword}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            <AnimatedLoginButton
              disabled={isLoading}
              isLoading={isLoading}
            />
            {loginByCodeEnabled() ? (
              <LoginByCodeForm initialEmail={email} disabled={isLoading} onSuccess={onSuccess} />
            ) : null}
            <div className="text-center">
              <div className="text-sm text-muted-foreground">
                {registrationClosed() ? (
                  <>
                    Don&apos;t have an account?{' '}
                    <Button
                      type="button"
                      variant="link"
                      size="inlineLink"
                      onClick={() => navigate('/register')}
                      disabled={isLoading}
                    >
                      {getSignupMode() === 'invite_only'
                        ? getAccessRequestConfig().submitLabel ?? 'Request operator access'
                        : getAccessRequestConfig().submitLabel ?? 'Request access'}
                    </Button>
                  </>
                ) : (
                  <>
                    Don't have an account?{' '}
                    <Button
                      type="button"
                      variant="link"
                      size="inlineLink"
                      onClick={() => navigate('/register')}
                      disabled={isLoading}
                    >
                      Sign up
                    </Button>
                  </>
                )}
              </div>
            </div>
            <AuthLegalLinks />
          </div>
         
        </div>
      </form>
    </div>
  )
}
